import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import i18n from '../i18n';
import { useAuth } from './AuthContext';
import { API_URL } from '../utils/api';
import { setAppBadgeCount } from '../utils/appBadge';

const NotificationContext = createContext(null);

let nextLocalId = 1;

function sendBrowserNotification(message) {
  if (!('Notification' in window)) return;
  if (document.hasFocus()) return; // Only when tab is not focused
  try {
    const settings = JSON.parse(localStorage.getItem('cs2_settings') || '{}');
    if (!settings.browser_notifications) return;
  } catch { return; }
  if (Notification.permission === 'granted') {
    new Notification('PartyFabriken CS2', { body: message, icon: '/stats/favicon.ico' });
  }
}

export function requestBrowserNotificationPermission() {
  if (!('Notification' in window)) return Promise.resolve('unsupported');
  if (Notification.permission === 'granted') return Promise.resolve('granted');
  if (Notification.permission === 'denied') return Promise.resolve('denied');
  return Notification.requestPermission();
}

/**
 * Konverterar en backend-notif till context-formen vi använder internt.
 * Frontend-komponenterna (Bell, Toast) använder `message`/`type`/`action`,
 * så vi behåller den shapen och lägger till `serverId` + `body` + `actionUrl`.
 */
function backendToLocal(n) {
  return {
    id: n.id,                       // server-id (sträng, prefix "notif_")
    serverId: n.id,                 // explicit för markRead/dismiss
    message: n.title,
    body: n.body || '',
    type: n.level || 'info',
    actionUrl: n.action_url || '',
    action: n.action_url ? { label: i18n.t('notify.open'), url: n.action_url } : null,
    createdAt: n.created_at ? new Date(n.created_at).getTime() : Date.now(),
    read: !!n.is_read,
    dismissed: !!n.is_dismissed,
  };
}

/**
 * Provider för notification-state.
 *
 * Hanterar både lokala toast-notiser (addNotification) och persisterade
 * server-notiser från PlayerNotification (initial fetch + SSE-push).
 * dismiss/markAllRead persisterar mot backend för server-notiser så att
 * tillståndet överlever sid-reload.
 */
export function NotificationProvider({ children }) {
  const { token, getAuthHeaders, isLoggedIn } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const sseRef = useRef(null);

  // Initial fetch när användaren loggar in.
  useEffect(() => {
    if (!isLoggedIn) {
      setNotifications([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/notifications/`, {
          headers: getAuthHeaders(),
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const initial = (data.notifications || []).map(backendToLocal);
        setNotifications(initial);
      } catch {
        // Tyst — SSE försöker direkt sen, ingen brådska
      }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn, getAuthHeaders]);

  // SSE-anslutning för live-push. EventSource stödjer inte custom headers,
  // så token skickas som query-param.
  useEffect(() => {
    if (!isLoggedIn || !token) {
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
      return;
    }

    const url = `${API_URL}/api/notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    sseRef.current = es;

    es.onmessage = (ev) => {
      try {
        const raw = JSON.parse(ev.data);
        const local = backendToLocal(raw);
        setNotifications(prev => {
          // Dedup om server redan finns
          if (prev.some(n => n.serverId === local.serverId)) return prev;
          return [local, ...prev];
        });
        // Browser notification om fliken inte är fokuserad
        sendBrowserNotification(local.message);
      } catch {
        // ignore
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnect:ar. Logga bara.
      // (Stänger inte här — om vi gör det måste vi sätta en backoff-timer.)
    };

    return () => {
      es.close();
      sseRef.current = null;
    };
  }, [isLoggedIn, token]);

  // Client-side toasts (fortsätter användas av andra komponenter).
  const addNotification = useCallback(({ message, type = 'info', duration = 8000, action = null }) => {
    const id = `local-${nextLocalId++}`;
    const notification = { id, message, type, action, createdAt: Date.now() };
    setNotifications(prev => [notification, ...prev]);

    sendBrowserNotification(message);

    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, dismissed: true } : n));
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== id));
        }, 300);
      }, duration);
    }

    return id;
  }, []);

  const dismissNotification = useCallback(async (id) => {
    const notif = notifications.find(n => n.id === id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, dismissed: true } : n));
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 300);

    // Persistera dismissal mot backend om det är en server-notif.
    if (notif?.serverId) {
      try {
        await fetch(`${API_URL}/api/notifications/dismiss/${notif.serverId}`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      } catch {
        // ignore — UI är redan dismissed, backend städas vid nästa session
      }
    }
  }, [notifications, getAuthHeaders]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter(n => !n.read && !n.dismissed).length;

  // Håll ikonens siffra i takt med klockan. Service workern sätter den vid
  // push (när appen är stängd); här nollas den så fort användaren faktiskt
  // läst, annars hade den legat kvar tills nästa notis kom.
  useEffect(() => {
    setAppBadgeCount(unreadCount);
  }, [unreadCount]);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    // Persistera mot backend för server-notiser.
    try {
      await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ all: true }),
      });
    } catch {
      // ignore
    }
  }, [getAuthHeaders]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      dismissNotification,
      clearAll,
      unreadCount,
      markAllRead,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
}
